﻿// Flattens a JSON value (object or array of objects) into CSV rows and triggers a download.
//
// Rules:
//  - Nested object keys become dot-separated headers (customer.address.city).
//  - Arrays expand to one row per element; sibling arrays produce a Cartesian product.
//  - Nested arrays recurse with the same Cartesian rule.
//  - Headers are the union of all leaf paths encountered.
//  - Missing values render as empty strings.
//  - RFC 4180 escaping; UTF-8 BOM is prepended so Excel (incl. localized BG) opens it correctly.

type JsonValue = string | number | boolean | null | undefined | JsonObject | JsonArray;
interface JsonObject { [k: string]: JsonValue }
interface JsonArray extends Array<JsonValue> {}

type FlatRow = Record<string, string | number | boolean | null>;

const isPlainObject = (v: any): v is JsonObject =>
    v !== null && typeof v === "object" && !Array.isArray(v);

// Flatten a single value at the given path prefix into a list of partial rows.
// Each returned row is one combination of array element choices below this node.
const flattenValue = (value: JsonValue, prefix: string): FlatRow[] => {
    if (value === null || value === undefined) {
        return [{ [prefix]: "" }];
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return [{ [prefix]: "" }];
        // one row set per element, then concatenated (vertical expansion)
        return value.flatMap(item => flattenValue(item, prefix));
    }

    if (isPlainObject(value)) {
        const entries = Object.entries(value);
        if (entries.length === 0) return [{ [prefix]: "" }];

        // Cartesian product across sibling keys.
        let rows: FlatRow[] = [{}];
        for (const [k, v] of entries) {
            const childPrefix = prefix ? `${prefix}.${k}` : k;
            const childRows = flattenValue(v, childPrefix);
            const merged: FlatRow[] = [];
            for (const r of rows) {
                for (const cr of childRows) {
                    merged.push({ ...r, ...cr });
                }
            }
            rows = merged;
        }
        return rows;
    }

    // primitive
    return [{ [prefix]: value as string | number | boolean }];
};

export const flattenJsonToRows = (data: JsonValue): { headers: string[]; rows: FlatRow[] } => {
    let rows: FlatRow[];

    if (Array.isArray(data)) {
        rows = data.flatMap(item => flattenValue(item, ""));
    } else if (isPlainObject(data)) {
        rows = flattenValue(data, "");
    } else {
        rows = [{ value: data as any }];
    }

    // Header union, preserving first-seen order.
    const headers: string[] = [];
    const seen = new Set<string>();
    for (const r of rows) {
        for (const k of Object.keys(r)) {
            if (!seen.has(k)) {
                seen.add(k);
                headers.push(k);
            }
        }
    }
    return { headers, rows };
};

// RFC 4180 escaping
const escapeCell = (val: unknown): string => {
    if (val === null || val === undefined) return "";
    let s: string;
    if (typeof val === "object") {
        try { s = JSON.stringify(val); } catch { s = String(val); }
    } else {
        s = String(val);
    }
    if (/[",\r\n]/.test(s)) {
        s = `"${s.replace(/"/g, '""')}"`;
    }
    return s;
};

export const buildCsv = (data: JsonValue): string => {
    const { headers, rows } = flattenJsonToRows(data);
    const lines: string[] = [];
    lines.push(headers.map(escapeCell).join(","));
    for (const r of rows) {
        lines.push(headers.map(h => escapeCell(r[h])).join(","));
    }
    return lines.join("\r\n");
};

const sanitizeFilename = (name: string): string =>
    name.replace(/[\\/:*?"<>|]+/g, "_").trim() || "data";

export const downloadJsonAsCsv = (data: JsonValue, filename = "data.csv"): void => {
    const csv = buildCsv(data);
    // UTF-8 BOM for Excel compatibility (incl. localized installs).
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename.toLowerCase().endsWith(".csv")
        ? sanitizeFilename(filename.replace(/\.csv$/i, "")) + ".csv"
        : sanitizeFilename(filename) + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
